import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';

import { UserId } from '@business/entities/account';
import { NoteAPI } from '@business/api/note';
import { NoteId, NoteResponse, NoteSearchParams } from '@business/entities/note';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class NoteBloc extends BaseBloc {
  private readonly noteRepo = new Repository<NoteResponse>({
    getItemId: note => note.id,
  });

  readonly notes$ = this.noteRepo.items$;
  readonly note$ = this.noteRepo.selectItem;

  onReset() {
    this.noteRepo.reset();
  }

  fetchNotes = (data?: NoteSearchParams) => {
    return NoteAPI.fetchNotes(data).pipe(this.noteRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_notes_error') }));
  };

  fetchNote = (id: NoteId) => {
    return NoteAPI.fetchNote(id).pipe(
      this.noteRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_notes_error') }),
    );
  };

  createNote = (checklist: Partial<NoteResponse>, userId?: UserId) => {
    return NoteAPI.createNote(checklist, userId).pipe(
      this.noteRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_note'),
      }),
    );
  };

  updateNote = (id: NoteId, data: Partial<NoteResponse>) => {
    return NoteAPI.updateNote(id, data).pipe(
      this.noteRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_note'),
      }),
    );
  };

  deleteNote = (id: NoteId) => {
    return showDeleteConfirmation('Delete Note', 'Do you really want to remove this note?').pipe(
      switchMap(() =>
        NoteAPI.deleteNote(id).pipe(
          this.noteRepo.ops.removeOne(() => id),
          handleMessage({
            type: requestMessage('delete_note'),
          }),
        ),
      ),
    );
  };
}
